<template>
  <div class="grid my-2">
    <div class="col-12 p-fluid field">
      <TreeInput
        :label="$t('Layer')"
        v-model="sourceLayer"
        :options="layers"
        label-key="title"
        model-type="object"
        :disable-branch-nodes="true"
      />
    </div>
    <div class="col-12 field" v-if="showMbTilesFolder">
      <TreeInput
        :label="$t('MBTile folder')"
        :placeholder="$t('MB tiles folder...')"
        v-model="selectedMBTilesFolder"
        :options="mbTilesFolders"
        label-key="value"
        item-key="value"
        :transform-to-tree="false"
        model-type="id"
      />
    </div>

    <div class="col-12 field" v-if="showMbTilesFolder">
      <TreeSelectInput
        :label="$t('Department')"
        :options="organizations"
        v-model="layer.organizationId"
        :placeholder="$t('Department')"
        id-key="organizationId"
        label-key="title"
        model-type="id"
        @change="onDepartmentChange"
      />
    </div>

    <div class="col-12 field" v-if="showMbTilesFolder">
      <TreeSelectInput
        :label="$t('Parent')"
        v-model="layer.parentDocumentId"
        :options="projects"
        :placeholder="$t('Choose option...')"
        id-key="documentId"
        label-key="title"
        model-type="id"
        @change="setParentLocalization"
      />
      <small v-if="!layer.organizationId">{{ $t('Select a department first...') }}</small>
    </div>
    <div class="col-12 p-fluid field" v-if="showMbTilesFolder">
      <label>{{ $t('Clipped image name') }}</label>
      <InputText type="text" v-model="layer.title" />
    </div>
  </div>
  <div class="flex justify-content-end mt-3">
    <Button
      :label="$t('Cancel')"
      icon="pi pi-times"
      class="p-button-danger border-noround-right"
      iconPos="left"
      @click="close"
    />
    <Button
      :label="$t('Save')"
      icon="pi pi-save"
      class="p-button-primary border-noround-left"
      iconPos="left"
      @click="save"
    />
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import featureProperties from '../../mixins/featureProperties.js';
import { MapController } from '@/js/Map/map-controller';
import TreeInput from '../inputs/TreeInput.vue';
import { flatTree, getNestedList } from '@/js/utils';
import TreeSelectInput from '../inputs/TreeSelectInput.vue';
import { mapGetters } from 'vuex';

export default defineComponent({
  name: 'MapLayerSelectionDialog',
  components: { TreeInput, TreeSelectInput },
  mixins: [featureProperties],
  inject: ['dialogRef'],
  computed: {
    ...mapGetters({
      common: 'GET_COMMON_DATA',
      currentProject: 'GET_CURRENT_PROJECT',
      user: 'GET_USER',
    }),
  },
  data() {
    return {
      mapController: undefined as MapController,
      layer: {} as Layer,
      sourceLayer: undefined as Layer,
      layers: [] as Layer[],
      mbTilesFolders: [],
      selectedMBTilesFolder: null,
      showMbTilesFolder: false,
      organizations: [],
      projects: [],
      filteredOrganizations: null,
    };
  },
  created() {
    if (this.dialogRef?.data?.layers) {
      this.layers = this.dialogRef.data.layers;
      if (this.layers?.length) this.updateTreeCompatibility(this.layers);
    }
    if (!this.layers?.length) this.getProjectsAndLayers();
  },
  mounted() {
    this.mapController = this.dialogRef.data.mapController;
    if (this.dialogRef?.data?.showMbTilesFolder) this.showMbTilesFolder = this.dialogRef?.data?.showMbTilesFolder;
    if (this.showMbTilesFolder) {
      this.getMBTilesFolders();
      this.getUserDepartments();
    }
  },
  methods: {
    close() {
      this.dialogRef.close();
    },
    getProjectsAndLayers() {
      this.$store
        .dispatch('getProjectsAndLayers', {
          query: {
            moduleId: this.$route?.meta?.moduleId,
          },
        })
        .then((response) => {
          this.layers = response;
          this.updateTreeCompatibility(this.layers);
        });
    },
    onDepartmentChange() {
      this.layer.parentDocumentId = null;
      this.getDestinationProjectsAndLayers();
    },
    save() {
      if (this.showMbTilesFolder) {
        if (!this.layer.title || this.layer.title.trim().length === 0) {
          this.$toast.add({
            severity: 'error',
            detail: this.$t('Clipped image name is required'),
            life: 5000,
          });
          return;
        }

        if (!this.layer.organizationId) {
          this.$toast.add({
            severity: 'error',
            detail: this.$t('Department is required'),
            life: 5000,
          });
          return;
        }

        if (!this.layer.parentDocumentId) {
          this.$toast.add({
            severity: 'error',
            detail: this.$t('Parent is required'),
            life: 5000,
          });
          return;
        }
      }

      if (!this.sourceLayer) {
        this.$toast.add({
          severity: 'error',
          detail: this.$t('A layer is required'),
          life: 5000,
        });
        return;
      }

      this.dialogRef.close({
        sourceLayer: this.sourceLayer,
        newLayer: this.layer,
        selectedMBTilesFolder: this.selectedMBTilesFolder,
      });
    },
    updateTreeCompatibility(layers) {
      layers.forEach((layer) => {
        if (layer.children?.length) {
          this.updateTreeCompatibility(layer.children);
        }
        if (this.dialogRef.data.compatibilityFn(layer)) {
          return;
        }

        Object.assign(layer, {
          disabled: true,
          disabledMessage: 'Layer is not compatible with current selection',
        });
      });
    },
    getMBTilesFolders() {
      this.$store
        .dispatch('getMBTilesFolders')
        .then((response) => {
          if (Array.isArray(response)) {
            this.mbTilesFolders = response;
          }
        })
        .catch((error) => {});
    },
    getUserDepartments() {
      this.$store
        .dispatch('getUserDepartments', {
          query: {
            moduleId: this.$route?.meta?.moduleId,
          },
        })
        .then((response) => {
          this.organizations = getNestedList(
            response.map((x) => ({
              organizationId: x.organizationId,
              parentOrganizationId: x.parentOrganizationId,
              depth: x.depth,
              title: x.title,
              id: x.organizationId,
              label: x.title,
            })),
            'organizationId',
            'parentOrganizationId',
            undefined,
            0
          );

          if (!this.data || !this.data.documentId) {
            if (!this.layer.organizationId && this.currentProject?.department?.organizationId) {
              this.layer.organizationId = this.currentProject?.department?.organizationId;
            }
          }

          if (this.layer.organizationId) {
            this.getDestinationProjectsAndLayers();
          }
        })
        .catch((error) => {
          this.getDestinationProjectsAndLayers();
        });
    },
    filterOrganizations(event) {
      this.filteredOrganizations = this.organizations.filter((x) =>
        x.label.toLowerCase().includes(event.query.toLowerCase())
      );
    },
    getProjects() {
      this.$store
        .dispatch('getUserProjects', {
          query: {
            moduleId: this.$route?.meta?.moduleId,
          },
        })
        .then((response) => {
          this.projects = response;
          this.setParentLocalization();
        })
        .catch((error) => {
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: this.$t(error),
            life: 5000,
          });
        });
    },
    getDestinationProjectsAndLayers() {
      this.$store
        .dispatch('getProjectsAndLayers', {
          query: {
            moduleId: this.$route?.meta?.moduleId,
          },
          data: {
            organizationIds: this.layer?.organizationId ? [this.layer.organizationId] : null,
          },
        })
        .then((response) => {
          this.projects = response;

          if (!this.data || !this.data.documentId) {
            if (!this.layer.parentDocumentId && this.currentProject) {
              this.layer.parentDocumentId = this.currentProject.documentId ? this.currentProject.documentId : null;
            }
          }

          this.setParentLocalization();
        })
        .catch((error) => {});
    },
    setParentLocalization() {
      if (this.layer?.parentDocumentId && !this.layer?.locationCoordinates?.lng) {
        const parent = flatTree(this.projects, []).find((p) => p.documentId === this.layer.parentDocumentId);
        if (parent && parent?.locationCoordinates?.lng && parent?.locationCoordinates?.lat) {
          this.layer.locationCoordinates = parent.locationCoordinates;
        }
      }
    },
  },
});
</script>
